import React from 'react';
import { create } from 'zustand';
import {
  ContentStudioStage1Request, ContentStudioStage1Response,
  ContentStudioStage2Request, ContentStudioStage2Response,
  ContentStudioStage3Request, ContentStudioStage3Response,
  ModuleDraft,
} from '@/common/api/models';

interface ContentStudioState {
  currentStage: number;
  // Configuration
  config: {
    moduleDepth: number;
    totalVolume: number;
    difficulty: 'Bachelor' | 'Master' | 'Professional';
    studyProgram: string;
  };
  // Stage 1
  stage1Input: Partial<ContentStudioStage1Request>;
  stage1Output: ContentStudioStage1Response | null;
  // Stage 2
  stage2Input: Partial<ContentStudioStage2Request>;
  stage2Output: ContentStudioStage2Response | null;
  // Stage 3
  stage3Input: Partial<ContentStudioStage3Request>;
  stage3Output: ContentStudioStage3Response | null;
  // Result
  moduleDraft: ModuleDraft | null;

  setStage: (stage: number) => void;
  setConfig: (data: Partial<ContentStudioState['config']>) => void;
  setStage1Input: (data: Partial<ContentStudioStage1Request>) => void;
  setStage1Output: (data: ContentStudioStage1Response) => void;
  setStage2Input: (data: Partial<ContentStudioStage2Request>) => void;
  setStage2Output: (data: ContentStudioStage2Response) => void;
  setStage3Input: (data: Partial<ContentStudioStage3Request>) => void;
  setStage3Output: (data: ContentStudioStage3Response) => void;
  setModuleDraft: (data: ModuleDraft) => void;
  reset: () => void;
}

export const useContentStudioStore = create<ContentStudioState>((set) => ({
  currentStage: 1,
  config: {
    moduleDepth: 3,
    totalVolume: 6,
    difficulty: 'Bachelor',
    studyProgram: '',
  },
  stage1Input: {},
  stage1Output: null,
  stage2Input: {},
  stage2Output: null,
  stage3Input: {},
  stage3Output: null,
  moduleDraft: null,

  setStage: (stage) => set({ currentStage: stage }),
  setConfig: (data) => set((state) => ({ config: { ...state.config, ...data } })),
  setStage1Input: (data) => set((state) => ({ stage1Input: { ...state.stage1Input, ...data } })),
  setStage1Output: (data) => set({ stage1Output: data }),
  setStage2Input: (data) => set((state) => ({ stage2Input: { ...state.stage2Input, ...data } })),
  setStage2Output: (data) => set({ stage2Output: data }),
  setStage3Input: (data) => set((state) => ({ stage3Input: { ...state.stage3Input, ...data } })),
  setStage3Output: (data) => set({ stage3Output: data }),
  setModuleDraft: (data) => set({ moduleDraft: data }),
  reset: () => set({
    currentStage: 1,
    config: {
      moduleDepth: 3,
      totalVolume: 6,
      difficulty: 'Bachelor',
      studyProgram: '',
    },
    stage1Input: {},
    stage1Output: null,
    stage2Input: {},
    stage2Output: null,
    stage3Input: {},
    stage3Output: null,
    moduleDraft: null,
  }),
}));

export const ContentStudioProvider = ({ children }: { children: React.ReactNode }) => {
  const reset = useContentStudioStore((state) => state.reset);
  React.useEffect(() => {
    reset(); // Reset on mount
  }, [reset]);
  return children;
};
